import { useState, useEffect, useCallback, useRef } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import QRCode from "react-qr-code";
import { Copy, Check, Shield, AlertCircle } from "lucide-react";
import { getMfaEnrollData, verifyMfaEnrollment } from "@/api/mfa";
import { useAuthStore } from "@/state/authStore";
import type { MfaEnrollResponse } from "@/types/mfa";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";

type Step = 1 | 2 | 3;

export default function MfaEnrollPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { from?: string })?.from ?? "/engage";

  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clearMfaFlags = useAuthStore(s => s.clearMfaFlags);

  const [step, setStep] = useState<Step>(1);
  const [enrollData, setEnrollData] = useState<MfaEnrollResponse | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [isLoadingEnroll, setIsLoadingEnroll] = useState(false);

  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);

  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const fetchEnrollData = useCallback(async () => {
    setIsLoadingEnroll(true);
    setEnrollError(null);
    try {
      const data = await getMfaEnrollData();
      setEnrollData(data);
    } catch {
      setEnrollError("Failed to load enrollment data. Please try again.");
    } finally {
      setIsLoadingEnroll(false);
    }
  }, []);

  useEffect(() => {
    fetchEnrollData();
  }, [fetchEnrollData]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrollData) return;
    setIsSubmittingCode(true);
    setCodeError(null);
    try {
      const result = await verifyMfaEnrollment({ code });
      setBackupCodes(result.backup_codes);
      setStep(3);
    } catch {
      setCode("");
      setCodeError("Invalid code. Check your authenticator app and try again.");
    } finally {
      setIsSubmittingCode(false);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please select and copy manually.");
    }
  };

  const handleDone = () => {
    clearMfaFlags();
    navigate(destination, { replace: true });
  };

  const stepLabels: Record<Step, string> = {
    1: "Scan QR Code",
    2: "Confirm Code",
    3: "Save Backup Codes",
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Shield className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center font-bold">
            Set Up Two-Factor Authentication
          </CardTitle>
          <CardDescription className="text-center">
            MFA is required for all Engage accounts. This keeps your data
            secure.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Step indicator */}
          <div className="flex items-center gap-2">
            {([1, 2, 3] as Step[]).map(s => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      step > s
                        ? "bg-primary text-primary-foreground"
                        : step === s
                          ? "bg-primary/10 text-primary border-2 border-primary"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {step > s ? <Check className="h-3.5 w-3.5" /> : s}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:block">
                    {stepLabels[s]}
                  </span>
                </div>
                {s < 3 && (
                  <div
                    className={`flex-1 h-0.5 mb-4 ${step > s ? "bg-primary" : "bg-border"}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1 — QR Code */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app (e.g. Google
                Authenticator, Authy).
              </p>

              {isLoadingEnroll && (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {enrollError && (
                <div className="space-y-3">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{enrollError}</AlertDescription>
                  </Alert>
                  <Button
                    variant="outline"
                    onClick={fetchEnrollData}
                    className="w-full"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {enrollData && (
                <div className="space-y-4">
                  <div className="flex justify-center p-4 bg-white border border-border rounded-lg">
                    <QRCode value={enrollData.otpauth_uri} size={180} />
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="flex items-center gap-2 bg-muted rounded-md px-3 py-2">
                      <code className="text-sm font-mono text-foreground break-all flex-1">
                        {enrollData.secret}
                      </code>
                      <button
                        type="button"
                        onClick={() =>
                          navigator.clipboard
                            .writeText(enrollData.secret)
                            .catch(() =>
                              toast.error(
                                "Copy failed — please select and copy manually."
                              )
                            )
                        }
                        className="text-muted-foreground hover:text-foreground flex-shrink-0"
                        aria-label="Copy secret"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <Button className="w-full" onClick={() => setStep(2)}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Confirm code */}
          {step === 2 && (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code shown in your authenticator app to
                confirm setup.
              </p>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={e => {
                  setCode(e.target.value.replace(/\D/g, ""));
                  if (codeError) setCodeError(null);
                }}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-input rounded-md focus:ring-2 focus:ring-ring bg-background"
                required
                autoFocus
              />

              {codeError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{codeError}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isSubmittingCode || code.length !== 6}
                >
                  {isSubmittingCode ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                      Confirming...
                    </span>
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Step 3 — Backup codes */}
          {step === 3 && (
            <div className="space-y-4">
              <Alert className="border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertDescription className="font-medium">
                  Save these backup codes somewhere safe. Each code can only be
                  used once.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((c, i) => (
                  <code
                    key={i}
                    className="text-sm font-mono bg-muted border border-border rounded px-2 py-1 text-center"
                  >
                    {c}
                  </code>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleCopyAll}
              >
                {copied ? (
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Copied!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Copy className="h-4 w-4" />
                    Copy all
                  </span>
                )}
              </Button>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={e => setAcknowledged(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-ring"
                />
                <span className="text-sm text-muted-foreground">
                  I've saved my backup codes in a safe place.
                </span>
              </label>

              <Button
                type="button"
                className="w-full"
                disabled={!acknowledged}
                onClick={handleDone}
              >
                Done — Go to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
